<template>
	<div id="rootApp">
		<popup-modal
			id="rootPopupModal"
			ref="rootPopupModal"
			:type="modalType"
			:title="modalTitle"
			:message="modalMessage"
			:content-class="modalContentClass"
			:primary-button-text="modalPrimaryButtonText"
			:secondary-button-text="modalSecondaryButtonText"
			@clickPrimary="rootPopupClickPrimary"
			@clickSecondary="rootPopupClickSecondary"
		>
		</popup-modal>
		<liquido-header></liquido-header>
		<router-view v-slot="{ Component }">
			<transition :name="transitionName">
				<component :is="Component" id="appContent" class="router-view container-lg" />
			</transition>
		</router-view>
		<mobile-debug-log v-if="showMobileDebugLog" ref="mobileDebugLogRef"></mobile-debug-log>
	</div>
</template>

<script>
/**
 * This rootApp is the root in the DOM tree of the LIQUIDO app.
 * It renders the header, the routed page with its slide left-right transition and the global popup for
 * infos and errors. The helpers every page reaches as this.$root.<name> - showError(), scrollToTop(),
 * the transition, ... - are shared with Polly, see src/shared/shell/app-shell.js. What is LIQUIDO's own is
 * here: the team backend's ping at the start, and the goto...() shortcuts.
 */
import liquidoHeader from "@/liquido/components/liquido-header.vue"
import popupModal from "@/shared/components/popup-modal.vue"
import mobileDebugLog from "@/shared/components/mobile-debug-log.vue"
import api from "@/liquido/services/liquido-graphql-client.js"
import config from "config"
import { createAppShell } from "@/shared/shell/app-shell.js"
import { loadBackendConfig } from "@/shared/shell/backend-config.js"

/** 
 * Pages will slide from right to left in this order 
 * Login and welcome page only do not slide, but fade in/out.
 * See router.js for the page names and their routes.
 */
const page_order = {
	"index": 0,
	"welcome": 1,
	"login": 1,     // welcome and login are on the same level, so they fade instead of sliding sideways
	"loginSms": 2,
	"forgotPassword": 2,
	"verifyEmail": 2,   // also reached anonymously from a mail link, same tier as forgotPassword
	"userhome": 9,
	"team": 10,
	"polls": 11,
	"newPoll": 12,
	"editPoll": 13,
	"showPoll": 13,
	"addProposal": 14,
	"startPoll": 14,    // forward from showPoll, back to it
	"castVote": 15,
	"pollWinner": 16,   // after castVote, so finishing a poll from the ballot slides forward to its winner
	// Legal pages, only linked from the bottom of team-home - not part of the main navigation flow
	"impressum": 20,
	"agb": 20,
	"datenschutz": 20,
}

/** Liquido Root App */
export default {
	i18n: {
		messages: {
			en: {
				// We carefully distinguish between these two cases!
				NetworkOffline: "You are offline. Please turn your WiFi on.",
				BackendNotReachable: "I cannot reach the LIQUIDO server right now. Please check your internet connection.",
				DEV_OpenGraphQL: "DEV HINT: Open /graphql/schema.graphql"
			},
			de: {
				// We carefully distinguish between these two cases!
				NetworkOffline: "Du bist offline. Bitte schalte dein WLAN ein.",
				BackendNotReachable: "Ich kann den LIQUIDO Server gerade nicht erreichen. Bitte prüfe ob du online bist.",
				DEV_OpenGraphQL: "DEV HINT: Open /graphql/schema.graphql"
			}
		},
	},
	name: "LiquidoApp",
	mixins: [createAppShell({ pageOrder: page_order })],
	components: { liquidoHeader, popupModal, mobileDebugLog },

	mounted() {
		// Check if we can reach the liquido backend
		api.pingApi()
			.then(() => {
				console.log("We are online and backend is reachable at "+config.LIQUIDO_API_URL)
				this.$refs.rootPopupModal.hide()
				return this.loadLiquidoConfig()
			})
			.catch(res => {
				if (res.response && res.response.status === 401) {
					console.log("Login is expired")
					if (this.$route.name !== "login") this.$router.push({name: "login"})
				} else {
					console.error("Cannot reach backend at "+config.LIQUIDO_API_URL, res)
					this.showWarning(this.$t("BackendNotReachable"));
				}
			})
	},
	methods: {
		//
		// These methods are available as this.$root.<method> in all vue sub components of root-app
		//
		/** The backend's validation rules over our own defaults, see src/shared/shell/backend-config.js */
		loadLiquidoConfig() {
			return loadBackendConfig(() => api.getLiquidoConfig())
		},

		gotoPoll(pollId) {
			const poll = api.getCachedPolls().find(p => p.id == pollId)
			if (poll?.status === "FINISHED") {
				this.$router.push({name: "pollWinner", params: {pollId: pollId}})
			} else if (poll?.status === "VOTING" && !poll?.userAlreadyVoted) {
				this.$router.push({name: "castVote", params: {pollId: pollId}})
			} else {
				this.$router.push({name: "showPoll", params: {pollId: pollId}})
			}
		},

		gotoPolls() {
			this.$router.push({name: "polls"})
		},

		gotoCreateNewPoll() {
			this.$router.push({name: "newPoll"})
		},

		gotoTeam() {
			this.$router.push({name: "team"})
		},
	},
}

</script>

<style>
/* All root layout styles are consolidated in liquido.css */
</style>
